import React, { useState } from "react";
import { StyleSheet, View } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import ThemedNavigationContainer from "./src/navigation/ThemedNavigationContainer";
import { linking } from "./src/navigation/routes";
import RootAppController from "./src/root/controllers/RootAppController";
import Toast from "react-native-toast-message";
import Logger from "./src/common/utils/Logger";
import { UserProvider } from "./src/context/UserContext";
import { ThemeProvider } from "./src/context/ThemeContext";
import RootAuthController from "./src/root/controllers/RootAuthController";
import { fetchPermissions } from "./src/api/rbac";
import {
  initializeNTConnect,
  AsyncStorageUserStorage,
  SessionStorageTokenStorage,
  getUserStorage,
} from "@ntconnect/framework";
import { updateFrameworkSettings } from "@ntconnect/framework/pbx";
import settings from "./src/config/settings";
import { hydratePreferences } from "./src/config/preferences";
import { isMultiExtension } from "./src/extensions/data/multiextensiondomains";

// Initialize the unified framework once at module load. PBX traffic goes
// through the fusionpbx-business-bff: per-user JWT bearer for auth, automatic
// /auth/refresh on 401, with single-flight refresh handled inside the client.
// SessionStorage = tokens survive page reloads but die on tab close. The
// BFF docs warn against localStorage for refresh tokens; this is the
// sanctioned compromise for SPA usage.
const tokenStorage = new SessionStorageTokenStorage("@maritime_");

initializeNTConnect({
  schema: "business",
  apiConfig: {
    pbx: {
      url: `${settings.apiUrl}/fusion/api`,
      authBaseUrl: settings.apiUrl,
      authType: "bearer_user",
    },
    notification: {
      url: settings.apiNotificationUrl,
      basicAuth: settings.apiNotificationAuth,
    },
    // AI assistant → BFF's transparent /anthropic proxy. bearer_user means the
    // shared client attaches the signed-in user's access token and runs the
    // standard 401 refresh-and-retry; the BFF injects the real Anthropic key.
    ai: {
      url: `${settings.apiUrl}/anthropic`,
      authBaseUrl: settings.apiUrl,
      authType: "bearer_user",
    },
    // ntconnect VoIP (trunk) → BFF's /ntconnect/* proxy. One VoIP per tenant;
    // provisioned when a tenant upgrades to a paid plan.
    ntconnect: {
      url: `${settings.apiUrl}/ntconnect`,
      authBaseUrl: settings.apiUrl,
      authType: "bearer_user",
    },
  },
  tokenStorage,
  userStorage: new AsyncStorageUserStorage(
    AsyncStorage,
    "@maritime_user_location_v6",
  ),
  pbxSettings: {
    accountPrefixPhy: settings.accountPrefixPhy,
    accountPrefixApp: settings.accountPrefixApp,
    multiExtension: false, // updated per-user in updateTenantSettings()
    // X-Client-Id sent on every /onboarding/* call. Must be in the BFF's
    // ONBOARDING_ALLOWED_CLIENTS env. Mirror this slug when adding new
    // self-signup surfaces.
    onboardingClientId: "voxcraft-web",
  },
  onUnauthorized: () => {
    // Refresh failed (or there was no refresh token to begin with). Drop
    // tokens + user so the auth gate in App() routes back to sign-in.
    Logger("BFF auth: onUnauthorized fired, signing user out");
    try {
      tokenStorage.clearTokens();
      getUserStorage().clear();
    } catch (err) {
      Logger("Failed to clear session on 401", err);
    }
    if (typeof window !== "undefined" && window.location) {
      window.location.reload();
    }
  },
});

export const navigationRef = React.createRef();

export default function App() {
  const [user, setUser] = useState();
  const [isReady, setIsReady] = useState(false);

  const updateTenantSettings = (user) => {
    if (user?.domain) {
      Logger(`Tenant active: ${user.domain}`);
      updateFrameworkSettings({
        multiExtension: isMultiExtension(user.domain),
      });
    }
  };

  const loadUser = async () => {
    // Hydrate user preferences (page size, landing route, etc.) before any
    // controllers mount so the first list fetch already uses the saved size.
    await hydratePreferences();

    let user = (await getUserStorage().getUser()) || {};

    // BFF tokens live in sessionStorage and die on tab close, while the user
    // blob lives in AsyncStorage and persists. On cold start without a token
    // we'd 401 the first permissions fetch and trigger an onUnauthorized
    // reload loop — short-circuit and force re-sign-in instead.
    const accessToken = await tokenStorage.getAccessToken();
    if (user.authenticated && !accessToken) {
      Logger("No BFF access token on cold start; clearing stale user");
      try { getUserStorage().clear(); } catch {}
      user = {};
    }

    updateTenantSettings(user);

    // Refresh the permissions for the user.
    if (user.userId && user.domain) {
      const permissions = await fetchPermissions(
        user.domain,
        user.userId,
        1,
        100000,
      );

      if (permissions.ok) {
        Logger(
          "Updated user permissions total",
          permissions.data?.data?.length,
        );
        user.permissions = permissions.data?.data;
      }
    }

    setUser(user);
    setIsReady(true);
    Logger("Current user loaded: ", user);
  };

  const updateUser = (user) => {
    if (user) getUserStorage().setUser(user);
    else getUserStorage().clear();

    updateTenantSettings(user);
    setUser(user);
  };

  if (!isReady) {
    //will fix this in a bit....
    loadUser();
    return null;
  }

  return (
    <View style={styles.container}>
      <ThemeProvider>
        <UserProvider
          value={{
            user,
            updateUser,
          }}
        >
          <ThemedNavigationContainer linking={linking} ref={navigationRef}>
            {user?.authenticated === true ? (
              <RootAppController />
            ) : (
              <RootAuthController />
            )}
            {/* <RootAppController /> */}
            <Toast />
          </ThemedNavigationContainer>
        </UserProvider>
      </ThemeProvider>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#040506",
  },
});
